import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { api } from '../api/client';

import { T } from '../styles/tokens';
import { Button, Input, Select, Textarea } from '../components/ui';
// ── Types ────────────────────────────────────────────────────────────────

interface TableInfo { name: string; rowCount: number; group: string }
interface ColInfo { cid: number; name: string; type: string; notnull: number; dflt_value: any; pk: number }
interface IdxInfo { seq: number; name: string; unique: number; origin: string; partial: number }
interface Activity { id: number; action: string; table_name: string; row_data: string | null; created_at: string }

// ── Styles (Cloudflare D1 / terminal) ────────────────────────────────────

const FONT = "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace";
const BG = T.bg;
const BG2 = T.bg;
const BORDER = T.border;
const MUTED = T.textMuted;
const TEXT = T.text;
const BRIGHT = T.text;
const BLUE = T.primary;
const GREEN = T.success;
const RED = T.danger;
const YELLOW = T.warning;

// ── Helpers ──────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr + (dateStr.includes('Z') ? '' : 'Z')).getTime();
  const s = Math.floor((now - then) / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function cellDisplay(value: any): any {
  if (value === null || value === undefined) return <span style={{ color: MUTED, fontStyle: 'italic', fontSize: 11 }}>null</span>;
  const s = String(value);
  if (s.length > 120) return <span title={s}>{s.substring(0, 120)}…</span>;
  return s;
}

function typeBadge(type: string): any {
  const c = type.includes('INT') ? BLUE : type.includes('TEXT') ? GREEN : type.includes('REAL') ? YELLOW : MUTED;
  return <span style={{ fontSize: 10, padding: '1px 5px', background: `${c}18`, color: c, borderRadius: 2, fontFamily: FONT }}>{type || 'ANY'}</span>;
}

// ── Component ────────────────────────────────────────────────────────────

export function AdminDatabase() {
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<'data' | 'schema' | 'sql'>('data');
  const [cols, setCols] = useState<ColInfo[]>([]);
  const [indexes, setIndexes] = useState<IdxInfo[]>([]);
  const [createSql, setCreateSql] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [sortCol, setSortCol] = useState('rowid');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // SQL editor
  const [sql, setSql] = useState('');
  const [qResults, setQResults] = useState<any[] | null>(null);
  const [qMeta, setQMeta] = useState<any>(null);
  const [qErr, setQErr] = useState('');
  const [qLoading, setQLoading] = useState(false);
  const [history, setHistory] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('d1_history') || '[]'); } catch { return []; }
  });

  // Inline edit
  const [editCell, setEditCell] = useState<{ r: number; c: string } | null>(null);
  const [editVal, setEditVal] = useState('');
  const editRef = useRef<HTMLInputElement>(null);

  // Activity feed
  const [activities, setActivities] = useState<Activity[]>([]);
  const actRef = useRef<HTMLDivElement>(null);

  // ── Fetch tables ───────────────────────────────────────────────────────
  const loadTables = useCallback(async () => {
    setLoadError('');
    try {
      const r: any = await api.get('/admin/database/tables');
      console.log('[DB Explorer] response:', r);
      const tables = r?.tables || r?.data?.tables;
      const totalRows = r?.totalRows || r?.data?.totalRows || 0;
      if (tables) {
        setTables(tables);
        setTotalRows(totalRows);
      } else {
        setLoadError('API retornou dados vazios. Response: ' + JSON.stringify(r).substring(0, 200));
      }
    } catch (err: any) {
      console.error('[DB Explorer] loadTables error:', err);
      setLoadError(err?.message || 'Erro ao carregar tabelas');
    }
  }, []);

  useEffect(() => { loadTables(); }, []);

  // ── Activity polling ───────────────────────────────────────────────────
  useEffect(() => {
    const poll = async () => {
      try {
        const r: any = await api.get('/admin/database/activity?limit=50');
        if (r?.activities) setActivities(r.activities);
      } catch { /* */ }
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => clearInterval(iv);
  }, []);

  // ── Fetch table data ──────────────────────────────────────────────────
  const fetchData = useCallback(async (name: string, p = 1, sort = 'rowid', dir = 'asc') => {
    setLoading(true);
    try {
      const [sr, rr] = await Promise.all([
        api.get(`/admin/database/tables/${name}/schema`) as any,
        api.get(`/admin/database/tables/${name}/rows?page=${p}&limit=50&sort=${sort}&order=${dir}`) as any,
      ]);
      if (sr) {
        setCols(sr.columns || []);
        setIndexes(sr.indexes || []);
        setCreateSql(sr.createStatement || '');
      }
      if (rr) {
        setRows(rr.rows || []);
        setTotal(rr.total || 0);
        setPage(rr.page || 1);
        setPages(rr.totalPages || 1);
      }
    } finally { setLoading(false); }
  }, []);

  const selectTable = (name: string) => {
    setSelected(name);
    setTab('data');
    setSortCol('rowid');
    setSortDir('asc');
    setEditCell(null);
    fetchData(name);
  };

  const handleSort = (col: string) => {
    const dir = sortCol === col && sortDir === 'asc' ? 'desc' : 'asc';
    setSortCol(col);
    setSortDir(dir);
    if (selected) fetchData(selected, 1, col, dir);
  };

  const goPage = (p: number) => { if (selected) fetchData(selected, p, sortCol, sortDir); };

  // ── Inline edit ────────────────────────────────────────────────────────
  const startEdit = (r: number, c: string, v: any) => {
    setEditCell({ r, c });
    setEditVal(v === null ? '' : String(v));
    setTimeout(() => editRef.current?.focus(), 30);
  };

  const saveEdit = async () => {
    if (!editCell || !selected) return;
    const pk = cols.find(c => c.pk)?.name;
    if (!pk) return;
    try {
      await api.put(`/admin/database/tables/${selected}/rows`, {
        primaryKey: pk, primaryValue: rows[editCell.r][pk], column: editCell.c, value: editVal || null,
      });
      fetchData(selected, page, sortCol, sortDir);
    } catch { /* */ }
    setEditCell(null);
  };

  const deleteRow = async (row: any) => {
    if (!selected) return;
    const pk = cols.find(c => c.pk)?.name;
    if (!pk || !confirm(`Delete row ${pk}=${row[pk]}?`)) return;
    try {
      await (api as any).delete(`/admin/database/tables/${selected}/rows`, { data: { primaryKey: pk, primaryValue: row[pk] } });
      fetchData(selected, page, sortCol, sortDir);
    } catch { /* */ }
  };

  // ── SQL exec ───────────────────────────────────────────────────────────
  const execSql = async () => {
    if (!sql.trim()) return;
    setQLoading(true); setQErr(''); setQResults(null); setQMeta(null);
    try {
      const r: any = await api.post('/admin/database/query', { sql, params: [] });
      if (r) { setQResults(r.results || []); setQMeta(r.meta || {}); }
      const h = [sql, ...history.filter(q => q !== sql)].slice(0, 30);
      setHistory(h);
      localStorage.setItem('d1_history', JSON.stringify(h));
    } catch (e: any) {
      setQErr(e?.response?.data?.error || e.message || 'Query failed');
    } finally { setQLoading(false); }
  };

  // ── Grouped tables ────────────────────────────────────────────────────
  const filtered = tables.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));
  const grouped = filtered.reduce<Record<string, TableInfo[]>>((a, t) => {
    (a[t.group] = a[t.group] || []).push(t); return a;
  }, {});
  const toggleGroup = (g: string) => {
    const s = new Set(collapsed); s.has(g) ? s.delete(g) : s.add(g); setCollapsed(s);
  };

  // ── Action icon ────────────────────────────────────────────────────────
  const actionIcon = (a: string) => {
    if (a === 'INSERT') return <span style={{ color: GREEN }}>●</span>;
    if (a === 'UPDATE') return <span style={{ color: YELLOW }}>●</span>;
    if (a === 'DELETE') return <span style={{ color: RED }}>●</span>;
    return <span style={{ color: MUTED }}>●</span>;
  };

  // visible columns (hide rowid from display if there's a pk)
  const displayCols = cols.filter(c => c.name !== 'rowid' || c.pk);

  // ══════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 56px)', background: BG, color: TEXT, fontFamily: FONT, fontSize: 13 }}>

      {/* ═══ LEFT: TABLE BROWSER ═══ */}
      <div style={{ width: 250, minWidth: 250, borderRight: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', background: BG2 }}>
        <div style={{ padding: '12px 12px 6px', fontSize: 11, fontWeight: 700, color: MUTED, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
          D1 Console
        </div>
        <div style={{ padding: '0 10px 8px' }}>
          <Input
            value={search} onChange={(v) => setSearch(v)}
            placeholder="Filter tables..."
            style={{ width: '100%', padding: '5px 8px', background: T.bgCard, border: `1px solid ${BORDER}`, color: TEXT, fontFamily: FONT, fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 6px' }}>
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([group, items]) => (
            <div key={group}>
              <div onClick={() => toggleGroup(group)} style={{ padding: '5px 6px', fontSize: 11, color: MUTED, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, userSelect: 'none' }}>
                <span style={{ fontSize: 9 }}>{collapsed.has(group) ? '' : '▼'}</span>
                <span style={{ flex: 1 }}>{group}</span>
                <span style={{ fontSize: 10, color: MUTED }}>{items.length}</span>
              </div>
              {!collapsed.has(group) && items.map(t => (
                <div key={t.name} onClick={() => selectTable(t.name)}
                  style={{ padding: '4px 8px 4px 18px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: selected === t.name ? BLUE : TEXT, background: selected === t.name ? `${BLUE}12` : 'transparent', fontSize: 12 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                  <span style={{ fontSize: 10, color: MUTED, flexShrink: 0, marginLeft: 4 }}>{t.rowCount >= 0 ? t.rowCount : '?'}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{ padding: '8px 12px', borderTop: `1px solid ${BORDER}`, fontSize: 11, color: MUTED }}>
          {tables.length} tables · {totalRows.toLocaleString()} rows
        </div>
      </div>

      {/* ═══ CENTER: MAIN AREA ═══ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {selected ? (
          <>
            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: `1px solid ${BORDER}`, background: BG2 }}>
              {(['data', 'schema', 'sql'] as const).map(t => (
                <div key={t} onClick={() => setTab(t)}
                  style={{ padding: '8px 16px', cursor: 'pointer', fontSize: 12, color: tab === t ? BLUE : MUTED, borderBottom: tab === t ? `2px solid ${BLUE}` : '2px solid transparent' }}>
                  {t === 'data' ? 'Data' : t === 'schema' ? 'Schema' : 'SQL'}
                </div>
              ))}
              <div style={{ flex: 1 }} />
              <div style={{ padding: '8px 14px', fontSize: 12, color: MUTED }}>
                <span style={{ color: BRIGHT }}>{selected}</span> · {total.toLocaleString()} rows
              </div>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              {/* ═══ DATA TAB ═══ */}
              {tab === 'data' && (
                <div>
                  <div style={{ overflow: 'auto', border: `1px solid ${BORDER}` }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {displayCols.map(col => (
                            <th key={col.name} onClick={() => handleSort(col.name)}
                              style={{ padding: '6px 10px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: MUTED, borderBottom: `1px solid ${BORDER}`, background: BG2, cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', position: 'sticky', top: 0 }}>
 {col.name}{col.pk ? ' ' : ''} {sortCol === col.name ? (sortDir === 'asc' ? '↑' : '↓') : ''} {typeBadge(col.type)}
                            </th>
                          ))}
                          <th style={{ padding: '6px 10px', width: 30, background: BG2, borderBottom: `1px solid ${BORDER}`, position: 'sticky', top: 0 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan={displayCols.length + 1} style={{ padding: 30, textAlign: 'center', color: MUTED }}>Loading...</td></tr>
                        ) : rows.length === 0 ? (
                          <tr><td colSpan={displayCols.length + 1} style={{ padding: 30, textAlign: 'center', color: MUTED }}>Empty table</td></tr>
                        ) : rows.map((row, ri) => (
                          <tr key={ri} style={{ background: ri % 2 ? T.bgCard : 'transparent' }}>
                            {displayCols.map(col => (
                              <td key={col.name} onDblClick={() => startEdit(ri, col.name, row[col.name])}
                                style={{ padding: '6px 10px', borderBottom: `1px solid ${BORDER}`, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}>
                                {editCell?.r === ri && editCell?.c === col.name ? (
                                  <input ref={editRef} value={editVal} onInput={(e: any) => setEditVal(e.target.value)}
                                    onBlur={saveEdit} onKeyDown={(e: any) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditCell(null); }}
                                    style={{ width: '100%', padding: '2px 4px', background: T.border, border: `1px solid ${BLUE}`, color: TEXT, fontFamily: FONT, fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                                ) : cellDisplay(row[col.name])}
                              </td>
                            ))}
                            <td style={{ padding: '6px 6px', borderBottom: `1px solid ${BORDER}`, textAlign: 'center' }}>
                              <span onClick={() => deleteRow(row)} style={{ cursor: 'pointer', color: RED, fontSize: 11, opacity: 0.5 }} title="Delete"></span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {pages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '10px 0', fontSize: 12, color: MUTED }}>
                      <span onClick={() => page > 1 && goPage(page - 1)} style={{ cursor: page > 1 ? 'pointer' : 'default', color: page > 1 ? BLUE : MUTED }}>← prev</span>
                      <span>{page}/{pages}</span>
                      <span onClick={() => page < pages && goPage(page + 1)} style={{ cursor: page < pages ? 'pointer' : 'default', color: page < pages ? BLUE : MUTED }}>next →</span>
                    </div>
                  )}
                </div>
              )}

              {/* ═══ SCHEMA TAB ═══ */}
              {tab === 'schema' && (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: MUTED, marginBottom: 6, textTransform: 'uppercase' }}>Columns</div>
                    <div style={{ border: `1px solid ${BORDER}` }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                          {['#', 'Name', 'Type', 'NOT NULL', 'Default', 'PK'].map(h => (
                            <th key={h} style={{ padding: '5px 10px', textAlign: 'left', fontSize: 11, color: MUTED, borderBottom: `1px solid ${BORDER}`, background: BG2 }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {cols.map((col, i) => (
                            <tr key={col.name} style={{ background: i % 2 ? T.bgCard : 'transparent' }}>
                              <td style={{ padding: '5px 10px', borderBottom: `1px solid ${BORDER}`, color: MUTED }}>{col.cid}</td>
                              <td style={{ padding: '5px 10px', borderBottom: `1px solid ${BORDER}`, color: BRIGHT, fontWeight: 600 }}>{col.name}</td>
                              <td style={{ padding: '5px 10px', borderBottom: `1px solid ${BORDER}` }}>{typeBadge(col.type)}</td>
                              <td style={{ padding: '5px 10px', borderBottom: `1px solid ${BORDER}`, color: col.notnull ? YELLOW : MUTED }}>{col.notnull ? 'YES' : '—'}</td>
                              <td style={{ padding: '5px 10px', borderBottom: `1px solid ${BORDER}`, color: GREEN }}>{col.dflt_value ?? <span style={{ color: MUTED }}>—</span>}</td>
 <td style={{ padding: '5px 10px', borderBottom: `1px solid ${BORDER}`, color: col.pk ? GREEN : MUTED }}>{col.pk ? '' : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {indexes.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, color: MUTED, marginBottom: 6, textTransform: 'uppercase' }}>Indexes</div>
                      <div style={{ border: `1px solid ${BORDER}` }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead><tr>
                            {['#', 'Name', 'Unique', 'Origin'].map(h => (
                              <th key={h} style={{ padding: '5px 10px', textAlign: 'left', fontSize: 11, color: MUTED, borderBottom: `1px solid ${BORDER}`, background: BG2 }}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {indexes.map((idx, i) => (
                              <tr key={idx.name} style={{ background: i % 2 ? T.bgCard : 'transparent' }}>
                                <td style={{ padding: '5px 10px', borderBottom: `1px solid ${BORDER}`, color: MUTED }}>{idx.seq}</td>
                                <td style={{ padding: '5px 10px', borderBottom: `1px solid ${BORDER}`, color: BRIGHT }}>{idx.name}</td>
                                <td style={{ padding: '5px 10px', borderBottom: `1px solid ${BORDER}`, color: idx.unique ? GREEN : MUTED }}>{idx.unique ? 'YES' : 'NO'}</td>
                                <td style={{ padding: '5px 10px', borderBottom: `1px solid ${BORDER}`, color: MUTED }}>{idx.origin}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {createSql && (
                    <div>
                      <div style={{ fontSize: 11, color: MUTED, marginBottom: 6, textTransform: 'uppercase' }}>CREATE Statement</div>
                      <pre style={{ padding: 12, background: BG2, border: `1px solid ${BORDER}`, color: TEXT, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}>{createSql}</pre>
                    </div>
                  )}
                </div>
              )}

              {/* ═══ SQL TAB ═══ */}
              {tab === 'sql' && (
                <div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    {history.length > 0 && (
                      <Select value="" onChange={(v) => { if (v) setSql(v); }}
                        placeholder={`History (${history.length})`}
                        style={{ padding: '4px 6px', background: BG2, border: `1px solid ${BORDER}`, color: TEXT, fontFamily: FONT, fontSize: 11 }}
                        options={history.map((q, i) => ({ value: q, label: q.substring(0, 50) }))} />
                    )}
                    <Select value="" onChange={(v) => { if (v) setSql(v); }}
                      placeholder="Templates"
                      style={{ padding: '4px 6px', background: BG2, border: `1px solid ${BORDER}`, color: TEXT, fontFamily: FONT, fontSize: 11 }}
                      options={[
                        { value: `SELECT * FROM ${selected || 'table'} LIMIT 50`, label: 'SELECT * LIMIT 50' },
                        { value: `SELECT COUNT(*) FROM ${selected || 'table'}`, label: 'COUNT(*)' },
                        { value: `PRAGMA table_info('${selected || 'table'}')`, label: 'PRAGMA table_info' },
                        { value: "SELECT name FROM sqlite_master WHERE type='table' ORDER BY name", label: 'List all tables' },
                      ]} />
                  </div>
                  <Textarea value={sql} onInput={(e: any) => setSql(e.target.value)}
                    onKeyDown={(e: any) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) execSql(); }}
                    placeholder="SELECT * FROM ..."
                    style={{ width: '100%', minHeight: 120, padding: 10, background: BG2, border: `1px solid ${BORDER}`, color: TEXT, fontFamily: FONT, fontSize: 13, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, marginBottom: 14 }}>
                    <Button variant="primary" size="sm" onClick={execSql} disabled={qLoading}
                      style={{ fontFamily: FONT }}>
                      {qLoading ? '...' : 'Run'}
                    </Button>
                    <span style={{ fontSize: 11, color: MUTED }}>Ctrl+Enter</span>
                  </div>
                  {qErr && <div style={{ padding: 8, background: `${RED}15`, border: `1px solid ${RED}40`, color: RED, fontSize: 12, marginBottom: 10, fontFamily: FONT }}>{qErr}</div>}
                  {qMeta && <div style={{ fontSize: 11, color: MUTED, marginBottom: 6 }}>{qMeta.rowCount} rows · {qMeta.duration}ms{qMeta.changes ? ` · ${qMeta.changes} changes` : ''}</div>}
                  {qResults && qResults.length > 0 && (
                    <div style={{ overflow: 'auto', border: `1px solid ${BORDER}` }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>{Object.keys(qResults[0]).map(k => <th key={k} style={{ padding: '5px 10px', textAlign: 'left', fontSize: 11, color: MUTED, borderBottom: `1px solid ${BORDER}`, background: BG2, whiteSpace: 'nowrap' }}>{k}</th>)}</tr></thead>
                        <tbody>
                          {qResults.map((row: any, ri: number) => (
                            <tr key={ri} style={{ background: ri % 2 ? T.bgCard : 'transparent' }}>
                              {Object.values(row).map((v: any, ci: number) => <td key={ci} style={{ padding: '5px 10px', borderBottom: `1px solid ${BORDER}`, fontSize: 12, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cellDisplay(v)}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {qResults && qResults.length === 0 && <div style={{ color: GREEN, fontSize: 12 }}>Query executed (no results)</div>}
                </div>
              )}
            </div>
          </>
        ) : (
          /* ═══ OVERVIEW ═══ */
          <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: BRIGHT, marginBottom: 16 }}>D1 Database Console</div>
            {loadError && (
              <div style={{ padding: 14, marginBottom: 16, background: T.dangerBg, border: `1px solid ${RED}`, borderRadius: 4, color: RED, fontSize: 13, fontFamily: FONT }}>
 {loadError}
                <Button variant="danger" size="xs" onClick={loadTables} style={{ marginLeft: 12 }}>Retry</Button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
              {[
                { label: 'Tables', value: tables.length, color: BLUE },
                { label: 'Total Rows', value: totalRows.toLocaleString(), color: GREEN },
                { label: 'Groups', value: Object.keys(grouped).length, color: YELLOW },
              ].map(s => (
                <div key={s.label} style={{ padding: '14px 20px', border: `1px solid ${BORDER}`, background: BG2 }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: MUTED }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginBottom: 6, textTransform: 'uppercase' }}>Top tables by rows</div>
            <div style={{ border: `1px solid ${BORDER}`, background: BG2 }}>
              {[...tables].sort((a, b) => b.rowCount - a.rowCount).slice(0, 15).map((t, i) => {
                const max = tables.reduce((m, x) => Math.max(m, x.rowCount), 1);
                return (
                  <div key={t.name} onClick={() => selectTable(t.name)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', borderBottom: `1px solid ${BORDER}` }}>
                    <span style={{ width: 160, fontSize: 12, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                    <div style={{ flex: 1, height: 10, background: T.bgCard }}>
                      <div style={{ width: `${Math.max(1, (t.rowCount / max) * 100)}%`, height: '100%', background: BLUE }} />
                    </div>
                    <span style={{ fontSize: 11, color: MUTED, minWidth: 40, textAlign: 'right' }}>{t.rowCount.toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
            {/* Quick SQL */}
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 11, color: MUTED, marginBottom: 6, textTransform: 'uppercase' }}>Quick Query</div>
              <Textarea value={sql} onInput={(e: any) => setSql(e.target.value)}
                onKeyDown={(e: any) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { setTab('sql'); execSql(); } }}
                placeholder="SELECT * FROM ..."
                style={{ width: '100%', minHeight: 80, padding: 10, background: BG2, border: `1px solid ${BORDER}`, color: TEXT, fontFamily: FONT, fontSize: 12, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
              <Button variant="primary" size="sm" onClick={() => { setTab('sql'); execSql(); }}
                style={{ marginTop: 6, fontFamily: FONT }}>
                Run
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ═══ RIGHT: ACTIVITY FEED ═══ */}
      <div style={{ width: 280, minWidth: 280, borderLeft: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', background: BG2 }}>
        <div style={{ padding: '10px 12px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: GREEN, display: 'inline-block', animation: 'pulse 2s infinite' }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Activity Feed</span>
          <span style={{ fontSize: 10, color: MUTED, marginLeft: 'auto' }}>{activities.length}</span>
        </div>
        <div ref={actRef} style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {activities.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: MUTED, fontSize: 11 }}>No activity yet</div>
          ) : activities.map(a => {
            let summary = '';
            try {
              const d = JSON.parse(a.row_data || '{}');
              const keys = Object.keys(d).slice(0, 3);
              summary = keys.map(k => {
                const v = d[k];
                const s = String(v ?? '');
                return `${k}: ${s.length > 30 ? s.substring(0, 30) + '…' : s}`;
              }).join(' · ');
            } catch { /* */ }
            return (
            <div key={a.id} onClick={() => selectTable(a.table_name)}
              style={{ padding: '6px 12px', cursor: 'pointer', borderBottom: `1px solid ${BORDER}`, fontSize: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {actionIcon(a.action)}
                <span style={{ fontWeight: 600, color: a.action === 'UPDATE' ? YELLOW : a.action === 'DELETE' ? RED : GREEN, fontSize: 10 }}>{a.action}</span>
                <span style={{ color: TEXT, fontWeight: 500 }}>{a.table_name}</span>
              </div>
              {summary && <div style={{ fontSize: 10, color: MUTED, marginTop: 2, paddingLeft: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{summary}</div>}
              <div style={{ fontSize: 10, color: MUTED, marginTop: 1, paddingLeft: 14 }}>{timeAgo(a.created_at)}</div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Pulse animation */}
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
    </div>
  );
}
